import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Award, Trophy, Star, Medal, Target, Flame, Sparkles, Loader2 } from 'lucide-react';
import { apiClient } from '../api/client';

const MILESTONE_BADGES = [
  { id: 'first-conversation', name: 'First Real Conversation', desc: 'Logged your first real-world conversation', icon: '💬', category: 'real-world', xp: 25 },
  { id: 'week-of-challenges', name: 'Challenge Champion', desc: 'Completed a full week of real-world challenges', icon: '🏆', category: 'real-world', xp: 100 },
  { id: 'ten-actions', name: 'Action Taker', desc: 'Logged 10 real-world social actions', icon: '🎯', category: 'real-world', xp: 50 },
  { id: 'going-solo', name: 'Going Solo', desc: '10 real-world wins in a row without a practice session', icon: '🦅', category: 'graduation', xp: 200 },
  { id: 'week-without-ava', name: 'Week Without AvA', desc: "A full week of real-world actions without needing a practice session", icon: '🌟', category: 'graduation', xp: 300 },
  { id: 'ready-for-real-world', name: 'Ready for the Real World', desc: 'Social Score above 80 with 50+ real-world actions — you may not need HiBFF anymore!', icon: '🎓', category: 'graduation', xp: 500 },
];

const Achievements = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.getChallengeStats()
      .then(s => setStats(s))
      .catch(() => setStats({ total_logged: 0, week_count: 0, real_world_streak: 0, total_xp: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const getEarnedStatus = (badge) => {
    if (!stats) return false;
    switch (badge.id) {
      case 'first-conversation': return stats.total_logged >= 1;
      case 'ten-actions': return stats.total_logged >= 10;
      case 'week-of-challenges': return stats.week_count >= 7;
      case 'going-solo': return stats.real_world_streak >= 10;
      case 'week-without-ava': return stats.real_world_streak >= 7;
      case 'ready-for-real-world': return stats.total_logged >= 50 && stats.total_xp >= 500;
      default: return false;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="achievements-page">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Confidence Tracker</h1>
        <p className="text-gray-600 mb-8">Milestones that celebrate your real-world progress, not app time</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Real-World Milestones */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="real-world-milestones">
            <div className="flex items-center gap-3 mb-4">
              <Trophy size={24} className="text-yellow-500" />
              <h3 className="text-lg font-bold text-gray-900">Real-World Milestones</h3>
            </div>
            <div className="space-y-3">
              {MILESTONE_BADGES.filter(b => b.category === 'real-world').map(badge => {
                const earned = getEarnedStatus(badge);
                return (
                  <div key={badge.id} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${earned ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`} data-testid={`badge-${badge.id}`}>
                    <span className={`text-2xl ${earned ? '' : 'grayscale opacity-40'}`}>{badge.icon}</span>
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${earned ? 'text-green-800' : 'text-gray-500'}`}>{badge.name}</p>
                      <p className="text-xs text-gray-500">{badge.desc}</p>
                    </div>
                    {earned && <Sparkles size={16} className="text-green-500" />}
                    <span className="text-xs font-bold text-purple-600">+{badge.xp} XP</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Graduation Milestones */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="graduation-milestones">
            <div className="flex items-center gap-3 mb-4">
              <Award size={24} className="text-purple-500" />
              <h3 className="text-lg font-bold text-gray-900">Graduation Milestones</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">The most powerful thing HiBFF can do is reward you for needing it less.</p>
            <div className="space-y-3">
              {MILESTONE_BADGES.filter(b => b.category === 'graduation').map(badge => {
                const earned = getEarnedStatus(badge);
                return (
                  <div key={badge.id} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${earned ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`} data-testid={`badge-${badge.id}`}>
                    <span className={`text-2xl ${earned ? '' : 'grayscale opacity-40'}`}>{badge.icon}</span>
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${earned ? 'text-purple-800' : 'text-gray-500'}`}>{badge.name}</p>
                      <p className="text-xs text-gray-500">{badge.desc}</p>
                    </div>
                    {earned && <Sparkles size={16} className="text-purple-500" />}
                    <span className="text-xs font-bold text-purple-600">+{badge.xp} XP</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rarity Tiers */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Star size={24} className="text-blue-500" />
              <h3 className="text-lg font-bold text-gray-900">Rarity Tiers</h3>
            </div>
            <p className="text-gray-600 mb-4">Badge rarity levels:</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Common (easy to earn)</li>
              <li>• Uncommon (moderate challenge)</li>
              <li>• Rare (significant achievement)</li>
              <li>• Epic (major milestone)</li>
              <li>• Legendary (elite status)</li>
            </ul>
          </div>

          {/* Community Progress */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Medal size={24} className="text-purple-500" />
              <h3 className="text-lg font-bold text-gray-900">Community Progress</h3>
            </div>
            <p className="text-gray-600 mb-4">How the community is growing together:</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Most real-world challenges completed this week</li>
              <li>• Weekly streak leaderboard</li>
              <li>• Monthly hearts earned</li>
              <li>• Anonymous rankings (privacy-first)</li>
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Achievements;
